import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Pause, Play, Zap, ExternalLink, HandCoins } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { PageSignalsLink } from '../../components/PageSignalsLink';
import { Pill } from '../../components/ui/Pill';
import {
  useCollections,
  useRunDunning,
  useRemindCollection,
  usePauseCollection,
} from '../../hooks/useCollections';
import { formatAmount, formatDate } from '../../lib/format';

type Tone = 'neutral' | 'accent' | 'success' | 'warn' | 'muted';
const STAGE_TONE = (s: number): Tone => (s >= 4 ? 'warn' : s >= 2 ? 'accent' : s >= 1 ? 'neutral' : 'muted');

export function CollectionsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const rows = useCollections(wsId);
  const runDunning = useRunDunning(wsId);
  const remind = useRemindCollection(wsId);
  const pause = usePauseCollection(wsId);

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="display text-2xl font-semibold">{t('collections.title')}</h1>
              <PageSignalsLink kinds={['overdue_ar']} />
            </div>
            <p className="text-sm text-ink-500 mt-1">{t('collections.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => runDunning.mutate()}
            disabled={runDunning.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            <Zap size={14} /> {t('collections.runSweep')}
          </button>
        </div>

        {runDunning.data && (
          <div className="mb-4 text-sm text-emerald-700 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
            {t('collections.sweepResult', { scanned: runDunning.data.scanned, sent: runDunning.data.sent })}
          </div>
        )}

        <div className="mb-6">
          <AiInsightCard
            agent="dispute_composer"
            title={t('aiOutput.cards.collections')}
            prompt={t('aiOutput.prompts.collections')}
          />
        </div>

        <Card>
          {rows.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !rows.data?.length ? (
            <EmptyState
              icon={HandCoins}
              title={t('emptyState.collections.title')}
              description={t('emptyState.collections.body')}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                    <th className="px-5 py-3 font-medium">{t('invoicesAp.invoiceNumber')}</th>
                    <th className="px-5 py-3 font-medium">{t('collections.customer')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.due')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('collections.daysOverdue')}</th>
                    <th className="px-5 py-3 font-medium">{t('collections.stage')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('invoicesAr.balance')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.map((r) => (
                    <tr key={r.id} className="border-b border-ink-200 dark:border-ink-800">
                      <td className="px-5 py-3">{r.invoice_number ?? '—'}</td>
                      <td className="px-5 py-3">
                        <div className="truncate max-w-[180px]">{r.customer_name ?? '—'}</div>
                        {!r.customer_email && (
                          <div className="text-[11px] text-warn-600">{t('collections.noEmail')}</div>
                        )}
                      </td>
                      <td className="px-5 py-3">{formatDate(r.due_date, i18n.language)}</td>
                      <td className="px-5 py-3 text-right num">{r.days_overdue}</td>
                      <td className="px-5 py-3">
                        <Pill tone={STAGE_TONE(r.dunning_stage)}>
                          {r.dunning_stage > 0 ? t('collections.stageN', { n: r.dunning_stage }) : t('collections.stageNone')}
                        </Pill>
                      </td>
                      <td className="px-5 py-3 text-right num">{formatAmount(r.balance_cents, r.currency, i18n.language)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => navigate(`/app/workspace/${wsId}/ar-invoices?open=${r.id}`)}
                            className="p-1.5 rounded-md text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/30"
                            title={t('collections.viewArInvoice')}
                          >
                            <ExternalLink size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => remind.mutate(r.id)}
                            disabled={remind.isPending || !r.customer_email}
                            className="p-1.5 rounded-md text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/30 disabled:opacity-40"
                            title={t('collections.remind')}
                          >
                            <Bell size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => pause.mutate({ id: r.id, paused: !r.dunning_paused })}
                            disabled={pause.isPending}
                            className="p-1.5 rounded-md text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800"
                            title={r.dunning_paused ? t('collections.resume') : t('collections.pause')}
                          >
                            {r.dunning_paused ? <Play size={14} /> : <Pause size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
